import React, { useState, useEffect, useRef } from 'react';
import {
    Typography, TextField, Button, IconButton, Divider, MenuItem, Chip, Box, Grid,
    FormControlLabel, Switch, Modal, Card, CardContent, Checkbox, FormGroup,
    FormControl, FormLabel, Alert, CircularProgress, Paper,
} from '@mui/material';
import {
    Edit, Save, Cancel, Work, Language, CalendarToday, Person,
    Description, Add, Delete, Visibility, Close,
    Gavel, VerifiedUser, Warning, CheckCircle, Badge, Info, Groups, CameraAlt, RestartAlt,
} from '@mui/icons-material';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import mammoth from 'mammoth/mammoth.browser';
import 'react-toastify/dist/ReactToastify.css';
import HeaderTopSection from '../../components/partials/HeaderTop';
import Banner from '../../components/partials/Banner';
import LoadingSpinner from '../../utils/LoadingSpinner';
import PhoneNumberInput from "../../utils/PhoneNumberInput";
import CountryInput from "../../utils/CountryInput";
import {
    useIdentificationPage,
    availableLanguages,
    formatDisplayDate,
    getFileType,
    identStyles,
    criminalStyles,
    PDF_ICON_URL,
    WORD_ICON_URL,
    ACCEPTED_IMAGE_ATTR,
    ACCEPTED_DOC_ATTR,
} from './hooks/Useidentificationpage';

// La configuration globale du worker pdf.js est maintenant gérée dans src/index.js

const PAGE_GRADIENT = 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)';

const cardSx = {
    background: '#ffffff',
    borderRadius: '18px',
    padding: { xs: '20px', sm: '26px', md: '30px' },
    boxShadow: '0 4px 20px rgba(16,24,40,0.07)',
    border: '1px solid rgba(16,24,40,0.04)',
};

const sectionHeaderSx = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '22px',
    paddingBottom: '14px',
    borderBottom: '2px solid #eef1f6',
    flexWrap: 'wrap',
};

const sectionIconSx = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 42, height: 42, borderRadius: '13px', flexShrink: 0,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff', boxShadow: '0 4px 12px rgba(102,126,234,0.35)',
};

const requiredChipSx = {
    background: '#fef2f2', color: '#e53935', fontWeight: 700,
    fontSize: 11, letterSpacing: '0.4px', height: 24,
};

const textFieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        background: '#fbfcfe',
    },
    mt: 1,
    mb: 1,
};

const SectionCard = ({ icon, title, required, children, sx = {} }) => (
    <Box sx={{ ...cardSx, ...sx }}>
        <Box sx={sectionHeaderSx}>
            <Box sx={sectionIconSx}>{icon}</Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a202c', flex: 1, minWidth: 120 }}>
                {title}
            </Typography>
            {required && <Chip label="Obligatoire" size="small" sx={requiredChipSx} />}
        </Box>
        {children}
    </Box>
);

const DisplayField = ({ label, value, icon, emptyText = 'Non spécifié' }) => (
    <Box className="display-field" sx={{ mb: 1.5 }}>
        <Typography variant="body2" color="textSecondary" className="field-label" sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 0.3 }}>
            {icon} {label}
        </Typography>
        <Typography variant="body1" className="field-value" sx={{ fontWeight: 500, color: '#1a202c' }}>
            {value || emptyText}
        </Typography>
    </Box>
);

const DateFields = ({ issueDateField, expiryDateField, issueDateLabel, expiryDateLabel, formData, dateErrors, handleDateChange }) => {
    const today = new Date().toISOString().split('T')[0];
    return (
        <div className="row g-3 mt-1 mb-2">
            <div className="col-12 col-sm-6">
                <TextField
                    fullWidth
                    label={issueDateLabel}
                    type="date"
                    sx={textFieldSx}
                    value={formData[issueDateField]}
                    onChange={e => handleDateChange(issueDateField, e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: today }}
                    error={!!dateErrors[issueDateField]}
                    helperText={dateErrors[issueDateField]}
                />
            </div>
            <div className="col-12 col-sm-6">
                <TextField
                    fullWidth
                    label={expiryDateLabel}
                    type="date"
                    sx={textFieldSx}
                    value={formData[expiryDateField]}
                    onChange={e => handleDateChange(expiryDateField, e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                        min: formData[issueDateField] ? (() => {
                            const d = new Date(formData[issueDateField]);
                            d.setDate(d.getDate() + 1);
                            return d.toISOString().split('T')[0];
                        })() : undefined
                    }}
                    error={!!dateErrors[expiryDateField]}
                    helperText={dateErrors[expiryDateField]}
                />
            </div>
        </div>
    );
};

const FileTypeIcon = ({ fileType, size = 60 }) => {
    if (fileType === 'pdf')  return <img src={PDF_ICON_URL}  alt="PDF"  style={{ width: size, height: size }} />;
    if (fileType === 'docx') return <img src={WORD_ICON_URL} alt="Word" style={{ width: size, height: size }} />;
    return <Description sx={{ fontSize: size * 0.8, color: 'grey.400' }} />;
};

const FileDisplay = ({ url, label, onPreview }) => {
    const fileType = getFileType(url);
    if (!url) return (
        <Box sx={{ p: 2.5, border: '1px dashed', borderColor: 'grey.300', borderRadius: '12px', textAlign: 'center', background: '#fafbfc' }}>
            <Typography variant="body2" color="textSecondary">Aucun fichier</Typography>
        </Box>
    );
    return (
        <Box
            onClick={() => onPreview(url, fileType, label)}
            sx={{
                display: 'flex', alignItems: 'center', gap: 2, p: 2, border: '1px solid', borderColor: 'grey.200',
                borderRadius: '12px', cursor: 'pointer', transition: 'box-shadow 0.15s, border-color 0.15s, transform 0.15s',
                background: '#fff',
                '&:hover': { boxShadow: '0 4px 14px rgba(16,24,40,0.1)', borderColor: 'primary.light', transform: 'translateY(-1px)' },
            }}
        >
            {fileType === 'image' ? (
                <img src={url} alt="Aperçu" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8 }} />
            ) : (
                <FileTypeIcon fileType={fileType} size={48} />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>{label || url.split('/').pop()}</Typography>
                <Typography variant="caption" color="textSecondary">
                    {fileType === 'image' ? 'Image' : fileType === 'pdf' ? 'Document PDF' : fileType === 'docx' ? 'Document Word' : 'Document'}
                </Typography>
            </Box>
            <IconButton size="small" color="primary" sx={{ pointerEvents: 'none' }}>
                <Visibility />
            </IconButton>
        </Box>
    );
};

const FileUploadZone = ({ fieldName, label, accept, required = false, styles, formData, handleFileUpload, handleRemoveFile, handleOpenPreview }) => {
    const file        = formData[fieldName];
    const filePreview = formData[`${fieldName}Preview`];
    const fileUrl     = formData[`${fieldName}Url`];

    const displayUrl = filePreview || fileUrl;
    const hasFile    = !!displayUrl;

    const previewType = filePreview === 'pdf'  ? 'pdf'
        : filePreview === 'docx' ? 'docx'
            : (filePreview?.startsWith('blob:')) ? 'image'
                : getFileType(fileUrl);

    const previewSrc = (filePreview && filePreview !== 'pdf' && filePreview !== 'docx')
        ? filePreview
        : fileUrl;

    const zoneStyle = required
        ? (hasFile ? (styles || identStyles).uploadZoneValid : (styles || identStyles).uploadZone)
        : (styles || identStyles).uploadZoneOptional;

    const s = styles || identStyles;

    return (
        <div style={zoneStyle}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                    {label}
                    {required && <span style={{ color: s.borderHex, marginLeft: 4 }}>*</span>}
                    {!required && <span style={{ color: '#999', marginLeft: 4, fontSize: '11px' }}>(optionnel)</span>}
                </Typography>
                {required && (
                    hasFile
                        ? <Box style={s.presentIndicator}><CheckCircle sx={{ fontSize: 15 }} /> Fourni</Box>
                        : <Box style={s.missingIndicator}><Warning sx={{ fontSize: 15 }} /> Manquant</Box>
                )}
            </Box>

            {hasFile ? (
                <Box sx={{ textAlign: 'center' }}>
                    {previewType === 'image' && previewSrc ? (
                        <Box sx={{ position: 'relative', display: 'inline-block', mb: 1 }}>
                            <img src={previewSrc} alt="Aperçu"
                                 style={{ maxWidth: '100%', maxHeight: 190, borderRadius: 12, cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,0.12)', display: 'block' }}
                                 onClick={() => handleOpenPreview(previewSrc, 'image', label)} />
                            <Box onClick={() => handleOpenPreview(previewSrc, 'image', label)}
                                 sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px', cursor: 'pointer', background: 'rgba(0,0,0,0)', transition: 'background 0.2s', '&:hover': { background: 'rgba(0,0,0,0.25)' }, '&:hover .preview-icon': { opacity: 1 } }}>
                                <Visibility className="preview-icon" sx={{ color: '#fff', fontSize: 30, opacity: 0, transition: 'opacity 0.2s' }} />
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ cursor: 'pointer', display: 'inline-block', mb: 1 }}
                             onClick={() => handleOpenPreview(displayUrl, previewType, label)}>
                            <FileTypeIcon fileType={previewType} size={68} />
                            <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#555' }}>
                                {file?.name || (previewType === 'pdf' ? 'Document PDF' : 'Document Word')}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1, flexWrap: 'wrap' }}>
                        <Button variant="outlined" component="label" size="small" sx={{ borderRadius: '999px' }}>
                            Remplacer
                            <input type="file" hidden accept={accept}
                                   onChange={e => handleFileUpload(e, fieldName)} />
                        </Button>
                        <Button variant="outlined" color="error" size="small" sx={{ borderRadius: '999px' }}
                                onClick={() => handleRemoveFile(fieldName)}>
                            Supprimer
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Button
                    fullWidth
                    variant={required ? 'contained' : 'outlined'}
                    component="label"
                    startIcon={<Description />}
                    color={required ? (styles === criminalStyles ? 'error' : 'primary') : 'inherit'}
                    sx={{ mt: 0.5, borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                >
                    Charger {label.toLowerCase()}
                    <input type="file" hidden accept={accept}
                           onChange={e => handleFileUpload(e, fieldName)} />
                </Button>
            )}
        </div>
    );
};

const PdfReader = ({ url }) => {
    const [blobUrl, setBlobUrl]   = useState(null);
    const [pdfError, setPdfError] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(true);
    const [numPages, setNumPages] = useState(0);
    const [pageWidth, setPageWidth] = useState(320);
    const containerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        let objectUrl = null;
        setPdfLoading(true); setPdfError(false); setBlobUrl(null); setNumPages(0);

        const load = async () => {
            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const blob = await response.blob();
                objectUrl = URL.createObjectURL(blob);
                if (!cancelled) setBlobUrl(objectUrl);
            } catch (e) {
                console.error('Erreur de lecture du document PDF :', e);
                if (!cancelled) setPdfError(true); setPdfLoading(false);
            }
        };
        if (url) load();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [url]);

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setPageWidth(containerRef.current.clientWidth - 16);
            }
        };
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, [blobUrl]);

    if (pdfError) {
        return (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', p: 4 }}>
                <img src={PDF_ICON_URL} alt="PDF" style={{ width: 56, height: 56, marginBottom: 12 }} />
                <Typography variant="h6">Aperçu indisponible</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Ce document PDF n'a pas pu être chargé pour lecture.
                </Typography>
            </Box>
        );
    }

    return (
        <Box ref={containerRef} sx={{ flex: 1, minHeight: 0, overflow: 'auto', background: '#f1f3f5', px: 1, py: 2 }}>
            {(pdfLoading || !blobUrl) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>Chargement du document…</Typography>
                </Box>
            )}
            {blobUrl && (
                <Document
                    file={blobUrl}
                    onLoadSuccess={({ numPages: n }) => { setNumPages(n); setPdfLoading(false); }}
                    onLoadError={(e) => { console.error('Erreur pdf.js :', e); setPdfError(true); setPdfLoading(false); }}
                    loading={null}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        {Array.from({ length: numPages }, (_, i) => (
                            <Box key={i} sx={{ boxShadow: '0 1px 6px rgba(0,0,0,0.15)', background: '#fff' }}>
                                <Page pageNumber={i + 1} width={pageWidth > 0 ? pageWidth : 320} />
                            </Box>
                        ))}
                    </Box>
                </Document>
            )}
        </Box>
    );
};

const DocxReader = ({ url }) => {
    const [html, setHtml] = useState(null);
    const [docError, setDocError] = useState(false);
    const [docLoading, setDocLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setDocLoading(true); setDocError(false); setHtml(null);

        const render = async () => {
            try {
                const response = await fetch(url);
                const arrayBuffer = await response.arrayBuffer();
                const result = await mammoth.convertToHtml({ arrayBuffer });
                if (!cancelled) setHtml(result.value);
            } catch (e) {
                console.error('Erreur de lecture du document Word :', e);
                if (!cancelled) setDocError(true);
            } finally {
                if (!cancelled) setDocLoading(false);
            }
        };
        if (url) render();
        return () => { cancelled = true; };
    }, [url]);

    if (docLoading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>Chargement du document…</Typography>
            </Box>
        );
    }
    if (docError) {
        return (
            <Box sx={{ textAlign: 'center', p: 4 }}>
                <img src={WORD_ICON_URL} alt="Word" style={{ width: 56, height: 56, marginBottom: 12 }} />
                <Typography variant="h6">Aperçu indisponible</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Ce document Word n'a pas pu être chargé pour lecture.
                </Typography>
            </Box>
        );
    }
    return (
        <Box
            sx={{
                px: { xs: 1, sm: 3 }, py: 2, maxHeight: '70vh', overflow: 'auto',
                background: '#fff', border: '1px solid', borderColor: 'grey.200', borderRadius: '8px',
                '& img': { maxWidth: '100%' },
            }}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
};

const DocumentPreviewModal = ({ docModal, handleCloseDocModal }) => {
    if (!docModal.open) return null;
    return (
        <div
            className="doc-preview-overlay"
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(15,23,42,0.75)', zIndex: 1300, overflowY: 'auto',
                backdropFilter: 'blur(2px)',
            }}
            onClick={handleCloseDocModal}
        >
            <div
                className="doc-preview-sheet"
                style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    backgroundColor: 'white', borderRadius: '24px 24px 0 0',
                    height: '95vh', maxHeight: '95vh', display: 'flex', flexDirection: 'column',
                    animation: 'docPreviewSlideUp 0.35s ease',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="doc-preview-header" style={{
                    flex: '0 0 auto', position: 'sticky', top: 0, zIndex: 5,
                    backgroundColor: 'white', borderBottom: '1px solid #e2e8f0',
                    borderTopLeftRadius: '24px', borderTopRightRadius: '24px',
                    padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        {docModal.type === 'pdf'
                            ? <img src={PDF_ICON_URL} alt="PDF" style={{ width: 26, height: 26, flexShrink: 0 }} />
                            : <img src={WORD_ICON_URL} alt="Word" style={{ width: 26, height: 26, flexShrink: 0 }} />}
                        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, color: '#1a202c' }}>
                            {docModal.label || (docModal.type === 'pdf' ? 'Document PDF' : 'Document Word')}
                        </Typography>
                    </Box>
                    <IconButton onClick={handleCloseDocModal} size="small"><Close /></IconButton>
                </div>

                <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {docModal.type === 'pdf' ? (
                        <PdfReader url={docModal.url} />
                    ) : docModal.type === 'docx' ? (
                        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                            <DocxReader url={docModal.url} />
                        </Box>
                    ) : null}
                </div>

                <div className="doc-preview-footer" style={{
                    flex: '0 0 auto', position: 'sticky', bottom: 0,
                    backgroundColor: 'white', borderTop: '1px solid #e2e8f0',
                    padding: '14px 20px', display: 'flex', justifyContent: 'flex-end',
                }}>
                    <Button variant="outlined" onClick={handleCloseDocModal} sx={{ borderRadius: 999, px: 3 }}>
                        Fermer
                    </Button>
                </div>
            </div>

            <style>{`
                @keyframes docPreviewSlideUp {
                    from { transform: translateY(100%); }
                    to { transform: translateY(0); }
                }
                @media (max-width: 600px) {
                    .doc-preview-sheet {
                        height: 100vh;
                        maxHeight: 100vh;
                        borderRadius: 0;
                    }
                    .doc-preview-header {
                        padding: 12px 14px;
                        borderRadius: 0 !important;
                    }
                    .doc-preview-header .MuiTypography-subtitle1 {
                        fontSize: 14px;
                    }
                    .doc-preview-footer {
                        padding: 10px 14px;
                    }
                }
            `}</style>
        </div>
    );
};

const ServiceSelectionModal = ({
                                   showServiceModal, handleCloseServiceModal, servicesLoading, servicesData,
                                   selectedService, setSelectedService, handleSelectService,
                                   selectedSubServices, handleSubServiceChange, handleSaveService,
                               }) => (
    <Modal open={showServiceModal} onClose={handleCloseServiceModal}>
        <Box sx={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
            width: { xs: '94vw', sm: 600, md: 800 }, maxWidth: '94vw',
            bgcolor: 'background.paper', boxShadow: 24, borderRadius: '18px',
            p: { xs: 2.5, sm: 4 }, maxHeight: '90vh', overflow: 'auto',
        }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }} gutterBottom>Sélectionnez un service</Typography>
            {servicesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <CircularProgress /><Typography sx={{ ml: 2 }}>Chargement des services...</Typography>
                </Box>
            ) : !selectedService ? (
                <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: 1 }}>
                    {Object.entries(servicesData).map(([key, service]) => (
                        <Grid item xs={12} sm={6} key={key}>
                            <Card sx={{
                                cursor: 'pointer', borderRadius: '14px', transition: 'all 0.25s',
                                border: '1px solid rgba(0,0,0,0.06)',
                                '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 22px rgba(16,24,40,0.12)' },
                            }}
                                  onClick={() => handleSelectService(key, service.id)}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <img src={service.icon} alt={service.name}
                                         style={{ height: 70, marginBottom: '1rem' }}
                                         onError={e => { e.target.src = 'assets/images/icon/default.png'; }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.15rem' } }}>{service.name}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5, flexWrap: 'wrap' }}>
                        <img src={servicesData[selectedService]?.icon} alt={servicesData[selectedService]?.name}
                             style={{ height: 52 }}
                             onError={e => { e.target.src = 'assets/images/icon/default.png'; }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {servicesData[selectedService]?.name} — Sélectionnez une ou plusieurs compétences
                        </Typography>
                    </Box>
                    <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend">Compétence disponibles</FormLabel>
                        <FormGroup>
                            {servicesData[selectedService]?.subServices?.map((sub, i) => (
                                <FormControlLabel key={i}
                                                  control={<Checkbox checked={selectedSubServices.includes(sub)} onChange={() => handleSubServiceChange(sub)} />}
                                                  label={sub} />
                            ))}
                        </FormGroup>
                    </FormControl>
                    <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <Button onClick={() => setSelectedService(null)} sx={{ borderRadius: '999px' }}>Retour</Button>
                        <Button variant="contained" sx={{ borderRadius: '999px' }} onClick={handleSaveService} disabled={selectedSubServices.length === 0}>
                            Enregistrer ({selectedSubServices.length} sélectionnés)
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    </Modal>
);

const SelectedServiceDisplay = ({ formData, servicesData, isEditMode, isProfessionalAccepted, handleRemoveService }) => {
    if (!formData.selectedService || !servicesData[formData.selectedService]) return null;
    const service = servicesData[formData.selectedService];
    return (
        <Card sx={{ mb: 2, border: '2px solid', borderColor: 'primary.main', borderRadius: '14px', overflow: 'hidden' }}>
            <CardContent sx={{ overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, minWidth: 0, flex: 1 }}>
                        <img src={service.icon} alt={service.name}
                             style={{ height: 56, flexShrink: 0 }}
                             onError={e => { e.target.src = 'assets/images/icon/default.png'; }} />
                        <Box sx={{ minWidth: 0, width: '100%' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, wordBreak: 'break-word', fontSize: { xs: '1rem', sm: '1.25rem' } }} gutterBottom>
                                {service.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">compétence :</Typography>
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {formData.subServices.map((sub, i) => (
                                    <Chip key={i} label={sub} size="small" sx={{ maxWidth: '100%', '& .MuiChip-label': { whiteSpace: 'normal', wordBreak: 'break-word' } }} />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                    {isEditMode && (
                        <IconButton onClick={handleRemoveService} color="error" sx={{ flexShrink: 0 }}>
                            <Delete />
                        </IconButton>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

const IdentificationSection = ({
                                   formData, setFormData, setIsDirty, shouldDisableIdentification, isEditMode,
                                   handleInputChange, handleDateChange, dateErrors, handleOpenPreview,
                                   handleFileUpload, handleRemoveFile,
                               }) => {
    return (
        <div id="identification-section" style={identStyles.sectionWrapper}>
            <Box style={identStyles.sectionTitle}>
                <Badge sx={{ color: identStyles.iconColor, fontSize: 26 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: identStyles.titleColor }}>
                    Pièce d'identité
                </Typography>
                <span style={identStyles.requiredBadge}>Obligatoire</span>
            </Box>

            {shouldDisableIdentification ? (
                <>
                    <div className="row g-3">
                        <div className="col-12 col-sm-6"><DisplayField label="Type de document" value={formData.documentType} icon={<Description sx={{ fontSize: 16 }} />} /></div>
                        <div className="col-12 col-sm-6"><DisplayField label="Nom du document" value={formData.documentName} icon={<Description sx={{ fontSize: 16 }} />} /></div>
                        <div className="col-12 col-sm-6"><DisplayField label="Autorité émettrice" value={formData.issuingAuthority} icon={<Description sx={{ fontSize: 16 }} />} /></div>
                        <div className="col-12 col-sm-6"><DisplayField label="Pays d'émission" value={formData.country} icon={<Description sx={{ fontSize: 16 }} />} /></div>
                        <div className="col-12 col-sm-6"><DisplayField label="Date de délivrance" value={formatDisplayDate(formData.documentIssueDate)} icon={<CalendarToday sx={{ fontSize: 16 }} />} /></div>
                        <div className="col-12 col-sm-6"><DisplayField label="Date d'expiration" value={formatDisplayDate(formData.documentExpiryDate)} icon={<CalendarToday sx={{ fontSize: 16 }} />} /></div>
                    </div>
                    <div className="row g-3 mt-1">
                        <div className="col-12 col-sm-6">
                            <Typography variant="body2" color="textSecondary" gutterBottom>Document recto ({formData.documentType})</Typography>
                            <FileDisplay url={formData.identificationFrontDocUrl} label="Recto" onPreview={handleOpenPreview} />
                        </div>
                        <div className="col-12 col-sm-6">
                            <Typography variant="body2" color="textSecondary" gutterBottom>Document verso</Typography>
                            <FileDisplay url={formData.identificationBackDocUrl} label="Verso" onPreview={handleOpenPreview} />
                        </div>
                    </div>
                </>
            ) : isEditMode ? (
                <>
                    <div className="row g-3">
                        <div className="col-12 col-sm-6">
                            <TextField fullWidth required select label="Type de document"
                                       name="documentType" value={formData.documentType} onChange={handleInputChange}
                                       variant="outlined" sx={textFieldSx}>
                                <MenuItem value="CNI">Carte nationale d'identité</MenuItem>
                                <MenuItem value="PASSPORT">Passeport</MenuItem>
                                <MenuItem value="DRIVING_LICENSE">Permis de conduire</MenuItem>
                            </TextField>
                        </div>
                        <div className="col-12 col-sm-6">
                            <TextField fullWidth label="Nom du document"
                                       name="documentName" value={formData.documentName} onChange={handleInputChange}
                                       variant="outlined" sx={textFieldSx} disabled />
                        </div>
                        <div className="col-12 col-sm-6">
                            <TextField fullWidth required label="Autorité émettrice *"
                                       name="issuingAuthority" value={formData.issuingAuthority} onChange={handleInputChange}
                                       variant="outlined" sx={textFieldSx} />
                        </div>
                        <div className="col-12 col-sm-6">
                            <Box sx={{ width: '100%', mt: 1 }}>
                                <CountryInput
                                    value={formData.country}
                                    onChange={(value) => {
                                        setFormData(prev => ({ ...prev, country: value }));
                                        setIsDirty(true);
                                    }}
                                    disabled={shouldDisableIdentification}
                                    required={true}
                                />
                            </Box>
                        </div>
                    </div>

                    <DateFields
                        issueDateField="documentIssueDate" expiryDateField="documentExpiryDate"
                        issueDateLabel="Date de délivrance" expiryDateLabel="Date d'expiration"
                        formData={formData} dateErrors={dateErrors} handleDateChange={handleDateChange}
                    />

                    <div className="row g-3 mt-1">
                        <div className="col-12 col-sm-6">
                            <FileUploadZone fieldName="identificationFrontDoc" label={`Recto (${formData.documentType})`} accept={ACCEPTED_DOC_ATTR} required styles={identStyles} formData={formData} handleFileUpload={handleFileUpload} handleRemoveFile={handleRemoveFile} handleOpenPreview={handleOpenPreview} />
                        </div>
                        <div className="col-12 col-sm-6">
                            <FileUploadZone fieldName="identificationBackDoc" label={`Verso (${formData.documentType})`} accept={ACCEPTED_DOC_ATTR} required={false} styles={identStyles} formData={formData} handleFileUpload={handleFileUpload} handleRemoveFile={handleRemoveFile} handleOpenPreview={handleOpenPreview} />
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="row g-3">
                        <div className="col-12 col-sm-6"><DisplayField label="Type de document" value={formData.documentType} icon={<Description sx={{ fontSize: 16 }} />} /></div>
                        <div className="col-12 col-sm-6"><DisplayField label="Nom du document" value={formData.documentName} icon={<Description sx={{ fontSize: 16 }} />} /></div>
                        <div className="col-12 col-sm-6"><DisplayField label="Autorité émettrice" value={formData.issuingAuthority} icon={<Description sx={{ fontSize: 16 }} />} /></div>
                        <div className="col-12 col-sm-6"><DisplayField label="Pays d'émission" value={formData.country} icon={<Description sx={{ fontSize: 16 }} />} /></div>
                        <div className="col-12 col-sm-6"><DisplayField label="Date de délivrance" value={formatDisplayDate(formData.documentIssueDate)} icon={<CalendarToday sx={{ fontSize: 16 }} />} /></div>
                        <div className="col-12 col-sm-6"><DisplayField label="Date d'expiration" value={formatDisplayDate(formData.documentExpiryDate)} icon={<CalendarToday sx={{ fontSize: 16 }} />} /></div>
                    </div>
                    <div className="row g-3 mt-1">
                        <div className="col-12 col-sm-6">
                            <Typography variant="body2" color="textSecondary" gutterBottom>Recto ({formData.documentType})</Typography>
                            <FileDisplay url={formData.identificationFrontDocUrl} label="Recto" onPreview={handleOpenPreview} />
                        </div>
                        <div className="col-12 col-sm-6">
                            <Typography variant="body2" color="textSecondary" gutterBottom>Verso</Typography>
                            <FileDisplay url={formData.identificationBackDocUrl} label="Verso" onPreview={handleOpenPreview} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

const CriminalRecordSection = ({
                                   formData, setFormData, setIsDirty, shouldDisableIdentification, isEditMode,
                                   handleDateChange, dateErrors, handleOpenPreview,
                                   handleFileUpload, handleRemoveFile, handleInputChange, handleResetCriminalRecord
                               }) => {
    return (
        <div id="criminal-record-section" style={criminalStyles.sectionWrapper}>
            <Box style={criminalStyles.sectionTitle}>
                <Gavel sx={{ color: criminalStyles.iconColor, fontSize: 26 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: criminalStyles.titleColor }}>
                    Bulletin n°3 du Casier judiciaire
                </Typography>
                <span style={criminalStyles.requiredBadge}>Facultatif</span>
            </Box>

            <Box style={criminalStyles.infoAlert}>
                <VerifiedUser sx={{ color: '#f59e0b', fontSize: 20, flexShrink: 0, mt: '1px' }} />
                <span>
                    Ce bloc n'est pas indispensable pour mettre à jour vos informations de professionnel et votre pièce d'identité, mais le casier judiciaire est indispensable pour que les administrateurs valident votre profil.
                </span>
            </Box>

            {shouldDisableIdentification ? (
                <>
                    <div className="row g-3">
                        <div className="col-12 col-sm-6"><DisplayField label="Nom du document" value="Bulletin n°3 du Casier judiciaire" icon={<Description sx={{ fontSize: 16 }} />} /></div>
                        <div className="col-12 col-sm-6"><DisplayField label="Autorité émettrice" value={formData.criminalRecordIssuingAuthority} icon={<Description sx={{ fontSize: 16 }} />} /></div>
                        <div className="col-12 col-sm-6"><DisplayField label="Pays d'émission" value={formData.criminalRecordCountry} icon={<Description sx={{ fontSize: 16 }} />} /></div>
                        <div className="col-12 col-sm-6"><DisplayField label="Date de délivrance" value={formatDisplayDate(formData.criminalRecordIssueDate)} icon={<CalendarToday sx={{ fontSize: 16 }} />} /></div>
                        <div className="col-12 col-sm-6"><DisplayField label="Date d'expiration" value={formatDisplayDate(formData.criminalRecordExpiryDate)} icon={<CalendarToday sx={{ fontSize: 16 }} />} /></div>
                    </div>
                    <div className="row g-3 mt-1">
                        <div className="col-12 col-sm-6">
                            <Typography variant="body2" color="textSecondary" gutterBottom>Bulletin n°3 du Casier judiciaire</Typography>
                            <FileDisplay url={formData.criminalRecordFrontDocUrl} label="Bulletin n°3 du Casier judiciaire" onPreview={handleOpenPreview} />
                        </div>
                    </div>
                </>
            ) : isEditMode ? (
                <>
                    <div className="row g-3">
                        <div className="col-12 col-sm-6">
                            <TextField fullWidth label="Nom du document" value="Bulletin n°3 du Casier judiciaire" variant="outlined" sx={textFieldSx} disabled />
                        </div>
                        <div className="col-12 col-sm-6">
                            <TextField fullWidth label="Autorité émettrice"
                                       name="criminalRecordIssuingAuthority" value={formData.criminalRecordIssuingAuthority} onChange={handleInputChange} variant="outlined" sx={textFieldSx} />
                        </div>
                        <div className="col-12 col-sm-6">
                            <Box sx={{ width: '100%', mt: 1 }}>
                                <CountryInput
                                    value={formData.criminalRecordCountry}
                                    onChange={(value) => {
                                        setFormData(prev => ({ ...prev, criminalRecordCountry: value }));
                                        setIsDirty(true);
                                    }}
                                    disabled={shouldDisableIdentification}
                                    required={false}
                                />
                            </Box>
                        </div>
                    </div>

                    <DateFields
                        issueDateField="criminalRecordIssueDate" expiryDateField="criminalRecordExpiryDate"
                        issueDateLabel="Date de délivrance du casier" expiryDateLabel="Date d'expiration du casier"
                        formData={formData} dateErrors={dateErrors} handleDateChange={handleDateChange}
                    />
                    <div className="row g-3 mt-1">
                        <div className="col-12 col-sm-6">
                            <FileUploadZone fieldName="criminalRecordFrontDoc" label="Bulletin n°3 du Casier judiciaire" accept={ACCEPTED_DOC_ATTR} required={false} styles={criminalStyles} formData={formData} handleFileUpload={handleFileUpload} handleRemoveFile={handleRemoveFile} handleOpenPreview={handleOpenPreview} />
                        </div>
                    </div>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button variant="text" color="error" startIcon={<RestartAlt />} onClick={handleResetCriminalRecord}>
                            Réinitialiser ce bloc
                        </Button>
                    </Box>
                </>
            ) : (
                <>
                    <div className="row g-3">
                        <div className="col-12 col-sm-6"><DisplayField label="Nom du document" value="Bulletin n°3 du Casier judiciaire" icon={<Description sx={{ fontSize: 16 }} />} /></div>
                        <div className="col-12 col-sm-6"><DisplayField label="Autorité émettrice" value={formData.criminalRecordIssuingAuthority} icon={<Description sx={{ fontSize: 16 }} />} /></div>
                        <div className="col-12 col-sm-6"><DisplayField label="Pays d'émission" value={formData.criminalRecordCountry} icon={<Description sx={{ fontSize: 16 }} />} /></div>
                        <div className="col-12 col-sm-6"><DisplayField label="Date de délivrance" value={formatDisplayDate(formData.criminalRecordIssueDate)} icon={<CalendarToday sx={{ fontSize: 16 }} />} /></div>
                        <div className="col-12 col-sm-6"><DisplayField label="Date d'expiration" value={formatDisplayDate(formData.criminalRecordExpiryDate)} icon={<CalendarToday sx={{ fontSize: 16 }} />} /></div>
                    </div>
                    <div className="row g-3 mt-1">
                        <div className="col-12 col-sm-6">
                            <Typography variant="body2" color="textSecondary" gutterBottom>Bulletin n°3 du Casier judiciaire</Typography>
                            <FileDisplay url={formData.criminalRecordFrontDocUrl} label="Bulletin n°3 du Casier judiciaire" onPreview={handleOpenPreview} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

const IdentificationPage = () => {
    const {
        isEditMode, setIsEditMode,
        showServiceModal,
        selectedService, setSelectedService,
        selectedSubServices,
        loading, saving, error, success, isDirty, setIsDirty,
        lightboxOpen, lightboxSlides, lightboxIndex, setLightboxIndex, handleCloseLightbox,
        docModal, handleCloseDocModal,
        mounted,
        servicesData, servicesLoading,
        isProfessionalAccepted,
        checkingProfessionalStatus,
        newLanguage,
        formData, setFormData,
        dateErrors,
        shouldDisableIdentification,
        handleDateChange,
        handleOpenPreview,
        handleFileUpload, handleRemoveFile,
        handleOpenServiceModal, handleCloseServiceModal, handleSelectService,
        handleSubServiceChange, handleSaveService, handleRemoveService,
        handleLanguageChange, handleRemoveLanguage,
        handleSave, handleCancel, handleInputChange, handleResetCriminalRecord,
    } = useIdentificationPage();

    if (loading || servicesLoading || checkingProfessionalStatus) {
        return (
            <>
                <HeaderTopSection />
                <Banner title="Mon Identification" />
                <div className="profile-main-content" style={{ background: PAGE_GRADIENT, minHeight: '100vh' }}>
                    <LoadingSpinner
                        isVisible={true}
                        loadingText={
                            loading ? "Chargement de vos données professionnelles..." :
                                servicesLoading ? "Chargement des services disponibles..." :
                                    "Vérification de votre statut..."
                        }
                    />
                </div>
            </>
        );
    }

    // ✅ Style dynamique pour le bouton Enregistrer (Sombre si désactivé, Vert si activé)
    const saveBtnSx = {
        borderRadius: '999px', textTransform: 'none', fontWeight: 600, px: 3,
        backgroundColor: '#2e7d32', // Vert
        color: '#ffffff',
        '&:hover': {
            backgroundColor: '#1b5e20',
        },
        '&.Mui-disabled': {
            backgroundColor: '#9e9e9e', // Gris sombre
            color: '#e0e0e0',
            boxShadow: 'none',
        }
    };

    return (
        <>
            <HeaderTopSection />
            <Banner title="Mon Identification" />
            <div className="profile-main-content" style={{
                background: PAGE_GRADIENT,
                minHeight: '100vh',
                padding: '32px 16px',
            }}>
                {mounted && (
                    <div className="profile-card" style={{ maxWidth: 1100, margin: '0 auto' }}>
                        <Box className="profile-card-content" sx={{
                            background: '#ffffff', borderRadius: '22px',
                            boxShadow: '0 10px 40px rgba(16,24,40,0.12)',
                            p: { xs: 2.5, sm: 4 },
                        }}>

                            {formData.status === 'REJECTED' && formData.rejectionReason && (
                                <Alert severity="error" icon={<Warning />} sx={{ mb: 2, borderRadius: '10px', alignItems: 'center' }}>
                                    <strong>Motif de rejet :</strong>&nbsp;{formData.rejectionReason}
                                </Alert>
                            )}

                            {error   && <Alert severity="error"   sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>}
                            {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}>{success}</Alert>}

                            <Box className="profile-header" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
                                <Typography variant="h4" className="profile-title" sx={{
                                    fontWeight: 800,
                                    background: 'linear-gradient(135deg, #1565c0 0%, #764ba2 100%)',
                                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                    fontSize: { xs: '1.5rem', sm: '2rem' },
                                }}>
                                    Information Complémentaire
                                </Typography>
                                {!isEditMode && (
                                    <Button className="profile-edit-btn" variant="contained" startIcon={<Edit />} onClick={() => setIsEditMode(true)} sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 600, px: 3 }}>
                                        Modifier
                                    </Button>
                                )}
                                {isProfessionalAccepted && <Chip label="Profil Validé" color="success" variant="filled" sx={{ fontWeight: 600 }} />}
                            </Box>

                            {isProfessionalAccepted && (
                                <Alert severity="info" sx={{ mb: 3, borderRadius: '10px' }}>
                                    Votre profil a été validé. Les blocs Identification et Casier Judiciaire sont verrouillés, mais vous pouvez toujours mettre à jour vos autres informations.
                                </Alert>
                            )}

                            <Box className="profile-form-grid" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                                <SectionCard icon={<Work />} title="Services" required>
                                    {isEditMode ? (
                                        <>
                                            {formData.selectedService && servicesData[formData.selectedService]
                                                ? <SelectedServiceDisplay formData={formData} servicesData={servicesData} isEditMode={isEditMode} isProfessionalAccepted={isProfessionalAccepted} handleRemoveService={handleRemoveService} />
                                                : <Button variant="outlined" startIcon={<Add />} onClick={handleOpenServiceModal} sx={{ mb: 1, borderRadius: '999px', textTransform: 'none' }}>Ajouter un service</Button>}
                                            <ServiceSelectionModal
                                                showServiceModal={showServiceModal} handleCloseServiceModal={handleCloseServiceModal} servicesLoading={servicesLoading} servicesData={servicesData} selectedService={selectedService} setSelectedService={setSelectedService} handleSelectService={handleSelectService} selectedSubServices={selectedSubServices} handleSubServiceChange={handleSubServiceChange} handleSaveService={handleSaveService}
                                            />
                                        </>
                                    ) : (
                                        formData.selectedService && servicesData[formData.selectedService]
                                            ? <SelectedServiceDisplay formData={formData} servicesData={servicesData} isEditMode={isEditMode} isProfessionalAccepted={isProfessionalAccepted} handleRemoveService={handleRemoveService} />
                                            : <DisplayField label="Service" value="Aucun service sélectionné" icon={<Work sx={{ fontSize: 16 }} />} />
                                    )}
                                </SectionCard>

                                <SectionCard icon={<Person />} title="Préférences de Travail" required>
                                    {isEditMode ? (
                                        <div className="row g-3 align-items-center">
                                            <div className="col-12 col-sm-6">
                                                <FormControlLabel
                                                    control={<Switch checked={formData.availabilityStatus} onChange={handleInputChange} name="availabilityStatus" color="primary" />}
                                                    label="Disponible pour travailler"
                                                />
                                            </div>
                                            <div className="col-12 col-sm-6">
                                                <TextField fullWidth required select label="Années d'expérience" name="yearsOfExperience" value={formData.yearsOfExperience !== '' ? formData.yearsOfExperience : ''} onChange={handleInputChange} variant="outlined" sx={textFieldSx}>
                                                    {Array.from({ length: 61 }, (_, i) => (
                                                        <MenuItem key={i} value={i}>{i} {i > 1 ? 'ans' : 'an'}</MenuItem>
                                                    ))}
                                                </TextField>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="row g-3">
                                            <div className="col-12 col-sm-6">
                                                <DisplayField label="Statut de disponibilité" value={formData.availabilityStatus ? 'Disponible' : 'Non disponible'} icon={<Person sx={{ fontSize: 16 }} />} />
                                            </div>
                                            <div className="col-12 col-sm-6">
                                                <DisplayField label="Années d'expérience" value={formData.yearsOfExperience !== '' && formData.yearsOfExperience !== null ? `${formData.yearsOfExperience} an${formData.yearsOfExperience > 1 ? 's' : ''}` : null} icon={<Work sx={{ fontSize: 16 }} />} />
                                            </div>
                                        </div>
                                    )}
                                </SectionCard>

                                <SectionCard icon={<Info />} title="À propos de vous" required>
                                    {isEditMode ? (
                                        <TextField fullWidth required multiline rows={4} label="À propos de vous" name="about" value={formData.about} onChange={handleInputChange} variant="outlined" sx={textFieldSx} />
                                    ) : (
                                        <DisplayField label="À propos de vous" value={formData.about} icon={<Person sx={{ fontSize: 16 }} />} />
                                    )}
                                </SectionCard>

                                <SectionCard icon={<Language />} title="Langues Parlées" required>
                                    {isEditMode ? (
                                        <Box className="chips-container">
                                            <Box className="chips-list" sx={{ mb: 2 }}>
                                                {formData.languages.map((lang, i) => (
                                                    <Chip key={i} label={lang}
                                                          onDelete={() => handleRemoveLanguage(lang)}
                                                          deleteIcon={<Cancel />}
                                                          className="language-chip" icon={<Language />} sx={{ m: 0.5 }} />
                                                ))}
                                            </Box>
                                            <TextField fullWidth select label="Ajouter une langue" value={newLanguage} onChange={handleLanguageChange} variant="outlined" sx={{ ...textFieldSx, maxWidth: 400 }}>
                                                <MenuItem value=""><em>Sélectionnez une langue</em></MenuItem>
                                                {availableLanguages.filter(l => !formData.languages.includes(l)).map((lang, i) => (
                                                    <MenuItem key={i} value={lang}>{lang}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Box>
                                    ) : (
                                        <Box className="chips-container">
                                            <Box className="chips-list">
                                                {formData.languages.map((lang, i) => (
                                                    <Chip key={i} label={lang} className="language-chip" icon={<Language />} variant="outlined" sx={{ m: 0.5 }} />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </SectionCard>

                                <SectionCard icon={<Description />} title="CV" required>
                                    {isEditMode ? (
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <FileUploadZone fieldName="cv" label="CV" accept={ACCEPTED_DOC_ATTR} required styles={identStyles} formData={formData} handleFileUpload={handleFileUpload} handleRemoveFile={handleRemoveFile} handleOpenPreview={handleOpenPreview} />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <FileDisplay url={formData.cvUrl} label="CV" onPreview={handleOpenPreview} />
                                            </div>
                                        </div>
                                    )}
                                </SectionCard>

                                <SectionCard icon={<CameraAlt />} title="Photo de profil & Plan de localisation" required>
                                    {isEditMode ? (
                                        <div className="row g-3 mt-1">
                                            <div className="col-12 col-sm-6">
                                                <FileUploadZone fieldName="profilePhoto" label="Photo de profil" accept={ACCEPTED_IMAGE_ATTR} required styles={identStyles} formData={formData} handleFileUpload={handleFileUpload} handleRemoveFile={handleRemoveFile} handleOpenPreview={handleOpenPreview} />
                                            </div>
                                            <div className="col-12 col-sm-6">
                                                <FileUploadZone fieldName="locationPlan" label="Plan de localisation" accept={ACCEPTED_DOC_ATTR} required styles={identStyles} formData={formData} handleFileUpload={handleFileUpload} handleRemoveFile={handleRemoveFile} handleOpenPreview={handleOpenPreview} />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="row g-3 mt-1">
                                            <div className="col-12 col-sm-6">
                                                <Typography variant="body2" color="textSecondary" gutterBottom>Photo de profil</Typography>
                                                <FileDisplay url={formData.profilePhotoUrl} label="Photo de profil" onPreview={handleOpenPreview} />
                                            </div>
                                            <div className="col-12 col-sm-6">
                                                <Typography variant="body2" color="textSecondary" gutterBottom>Plan de localisation</Typography>
                                                <FileDisplay url={formData.locationPlanUrl} label="Plan de localisation" onPreview={handleOpenPreview} />
                                            </div>
                                        </div>
                                    )}
                                </SectionCard>

                                <SectionCard icon={<Groups />} title="Contacts d'urgence" required>
                                    {isEditMode ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>Contact 1</Typography>
                                                <div className="row g-3">
                                                    <div className="col-12 col-sm-6">
                                                        <TextField fullWidth required label="Nom" name="contact1Name" value={formData.contact1Name} onChange={handleInputChange} variant="outlined" sx={textFieldSx} />
                                                    </div>
                                                    <div className="col-12 col-sm-6">
                                                        <TextField fullWidth label="Relation" name="contact1Relationship" value={formData.contact1Relationship} onChange={handleInputChange} variant="outlined" sx={textFieldSx} />
                                                    </div>
                                                    <div className="col-12">
                                                        <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>Téléphone <span style={{ color: 'red' }}>*</span></Typography>
                                                        <PhoneNumberInput
                                                            value={formData.contact1Phone}
                                                            onChange={(value) => {
                                                                setFormData(prev => ({ ...prev, contact1Phone: value }));
                                                                setIsDirty(true);
                                                            }}
                                                            defaultCountry="CM"
                                                        />
                                                    </div>
                                                </div>
                                            </Box>
                                            <Divider />
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>Contact 2</Typography>
                                                <div className="row g-3">
                                                    <div className="col-12 col-sm-6">
                                                        <TextField fullWidth required label="Nom" name="contact2Name" value={formData.contact2Name} onChange={handleInputChange} variant="outlined" sx={textFieldSx} />
                                                    </div>
                                                    <div className="col-12 col-sm-6">
                                                        <TextField fullWidth label="Relation" name="contact2Relationship" value={formData.contact2Relationship} onChange={handleInputChange} variant="outlined" sx={textFieldSx} />
                                                    </div>
                                                    <div className="col-12">
                                                        <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>Téléphone <span style={{ color: 'red' }}>*</span></Typography>
                                                        <PhoneNumberInput
                                                            value={formData.contact2Phone}
                                                            onChange={(value) => {
                                                                setFormData(prev => ({ ...prev, contact2Phone: value }));
                                                                setIsDirty(true);
                                                            }}
                                                            defaultCountry="CM"
                                                        />
                                                    </div>
                                                </div>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <div className="row g-3">
                                            <div className="col-12 col-sm-6">
                                                <DisplayField label="Contact 1" value={`${formData.contact1Name} (${formData.contact1Relationship})`} icon={<Person sx={{ fontSize: 16 }} />} />
                                                <DisplayField label="Téléphone 1" value={formData.contact1Phone} icon={<Person sx={{ fontSize: 16 }} />} />
                                            </div>
                                            <div className="col-12 col-sm-6">
                                                <DisplayField label="Contact 2" value={`${formData.contact2Name} (${formData.contact2Relationship})`} icon={<Person sx={{ fontSize: 16 }} />} />
                                                <DisplayField label="Téléphone 2" value={formData.contact2Phone} icon={<Person sx={{ fontSize: 16 }} />} />
                                            </div>
                                        </div>
                                    )}
                                </SectionCard>

                                <IdentificationSection
                                    formData={formData}
                                    setFormData={setFormData}
                                    setIsDirty={setIsDirty}
                                    shouldDisableIdentification={shouldDisableIdentification}
                                    isEditMode={isEditMode}
                                    handleInputChange={handleInputChange}
                                    handleDateChange={handleDateChange}
                                    dateErrors={dateErrors}
                                    handleOpenPreview={handleOpenPreview}
                                    handleFileUpload={handleFileUpload}
                                    handleRemoveFile={handleRemoveFile}
                                />

                                <CriminalRecordSection
                                    formData={formData}
                                    setFormData={setFormData}
                                    setIsDirty={setIsDirty}
                                    shouldDisableIdentification={shouldDisableIdentification}
                                    isEditMode={isEditMode}
                                    handleInputChange={handleInputChange}
                                    handleDateChange={handleDateChange}
                                    dateErrors={dateErrors}
                                    handleOpenPreview={handleOpenPreview}
                                    handleFileUpload={handleFileUpload}
                                    handleRemoveFile={handleRemoveFile}
                                    handleResetCriminalRecord={handleResetCriminalRecord}
                                />

                            </Box>

                            <Box className="profile-stats" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 4 }}>
                                {[
                                    { label: 'Langues', value: formData.languages.length, color: '#2196f3' },
                                    { label: "Années d'expérience", value: formData.yearsOfExperience !== '' && formData.yearsOfExperience !== null ? formData.yearsOfExperience : '0', color: '#4caf50' },
                                    { label: 'Disponible', value: formData.availabilityStatus ? 'Oui' : 'Non', color: formData.availabilityStatus ? '#4caf50' : '#ff9800' },
                                ].map((stat, i) => (
                                    <Box key={i} className="profile-stat-item" sx={{ flex: '1 1 150px', textAlign: 'center', p: 2, borderRadius: '14px', background: '#f8fafc', border: '1px solid #eef1f6' }}>
                                        <Typography variant="h6" className="profile-stat-value" style={{ color: stat.color, fontWeight: 800 }}>{stat.value}</Typography>
                                        <Typography variant="body2" className="profile-stat-label" color="textSecondary">{stat.label}</Typography>
                                    </Box>
                                ))}
                            </Box>

                            {isEditMode && (
                                <Box className="profile-actions" sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap', mt: 4 }}>
                                    <Button variant="outlined" startIcon={<Cancel />} onClick={handleCancel} className="profile-cancel-btn" disabled={saving} sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 600, px: 3 }}>
                                        Annuler
                                    </Button>
                                    <Button variant="contained" startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />} onClick={handleSave} className="profile-save-btn" disabled={saving || !isDirty} sx={saveBtnSx}>
                                        {saving ? 'Sauvegarde...' : 'Enregistrer'}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </div>
                )}

                <Lightbox
                    open={lightboxOpen} close={handleCloseLightbox} slides={lightboxSlides} index={lightboxIndex}
                    on={{ view: ({ index }) => setLightboxIndex(index) }} plugins={[Zoom]} zoom={{ maxZoomPixelRatio: 3 }}
                    controller={{ closeOnBackdropClick: true }} render={{ buttonDownload: () => null }}
                />

                <DocumentPreviewModal docModal={docModal} handleCloseDocModal={handleCloseDocModal} />
            </div>

            {saving && <LoadingSpinner isVisible={true} loadingText="Sauvegarde en cours..." />}
        </>
    );
};

export default IdentificationPage;